import { applyRevision } from "@/data-preparation-tool/editing/apply-revision";
import { revisionToEdit } from "@/data-preparation-tool/inspect-and-publish/refine-and-publish-utils";
import { loadRevisionDataThunk } from "@/data-preparation-tool/loading/load-revision-data-thunk";
import { selectValidRevisionEntities } from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-selectors";
import { setEntityIdToOpenContextMenu } from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-slice";
import {
  selectRegistrationRevision,
  selectRootEntity,
} from "@/data-preparation-tool/store/revision/revision-entity-selectors";
import { addEntities } from "@/data-preparation-tool/store/revision/revision-slice";
import {
  sanitizeName,
  validateScanTreeElementName,
} from "@/data-preparation-tool/ui/scan-tree/scan-tree-context-menu-action-items/edit-name-context-menu-action-item";
import type { RootState } from "@/store/store";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import {
  ActionTextField,
  CircularProgress,
  FaroMenu,
  FaroMenuItem,
  NoTranslate,
  PlusIcon,
} from "@faro-lotv/flat-ui";
import { generateGUID, type GUID } from "@faro-lotv/foundation";
import {
  CaptureTreeEntityType,
  RegistrationStatus,
  RevisionStatus,
  useApiClientContext,
  type CaptureTreeEntityRevision,
  type ProjectApi,
  type RegistrationRevision,
} from "@faro-lotv/service-wires";
// eslint-disable-next-line no-restricted-imports -- FaroMenuItem cannot host the inline name editor (ST-5157)
import { MenuItem } from "@mui-wrapper";
import {
  useCallback,
  useMemo,
  useState,
  type JSX,
  type MouseEvent,
} from "react";
import { slotProps } from "../scan-tree-context-menu-base";
type ClusterEntityRevision = CaptureTreeEntityRevision & {
  type: CaptureTreeEntityType.cluster;
};

interface AddClusterContextMenuActionItemProps {
  /**
   * Parent element to which the cluster needs to be added. If not provided, the cluster will be added to the root of the scan tree.
   */
  clusterEntity?: CaptureTreeEntityRevision;

  /** whether the add cluster action is disabled */
  disabled?: boolean;

  /** Callback executed to handle the cluster addition */
  onClusterAddition?(): void;

  /** Callback executed to expand the cluster parent node in the tree. */
  onExpandParentNode?(entityId: GUID): void;
}

/**
 * @returns a context menu for adding a cluster to the root of the scan tree
 */
export function AddClusterContextMenuActionItem({
  clusterEntity,
  disabled,
  onClusterAddition,
  onExpandParentNode,
}: AddClusterContextMenuActionItemProps): JSX.Element | null {
  const rootEntityId = useAppSelector(selectRootEntity)?.id;
  const parentId = clusterEntity?.id ?? rootEntityId ?? null;
  const { projectApiClient } = useApiClientContext();
  const revisionEntities = useAppSelector(selectValidRevisionEntities);
  const dispatch = useAppDispatch();
  const { getState } = useAppStore();

  const [isProcessing, setIsProcessing] = useState(false);
  const [nameEditorAnchorEl, setNameEditorAnchorEl] =
    useState<HTMLElement | null>(null);

  const siblingEntities = useMemo(
    () => revisionEntities.filter((entity) => entity.parentId === parentId),
    [parentId, revisionEntities],
  );
  const siblingEntityNames = useMemo(
    () => siblingEntities.map((entity) => entity.name),
    [siblingEntities],
  );
  const defaultClusterName = useMemo(
    () => getNextDefaultClusterName(parentId, siblingEntities),
    [parentId, siblingEntities],
  );

  const createCluster = useCallback(
    async (clusterName: string) => {
      if (!parentId || isProcessing) return;
      const dateString = new Date().toISOString();
      const userId = generateGUID();
      const sanitizedClusterName = sanitizeName(clusterName);
      const state = getState();
      const currentRevision = selectRegistrationRevision(state);
      if (!currentRevision) return;
      const newCluster: ClusterEntityRevision = {
        id: generateGUID(),
        createdAt: dateString,
        createdBy: userId,
        lastPatchedAt: dateString,
        lastPatchedBy: userId,
        status: RevisionStatus.added,
        type: CaptureTreeEntityType.cluster,
        parentId,
        name: sanitizedClusterName,
        // Inherit the parent's manual lock status if it is locked,
        // Other statuses (registered/unverified/unregistered) are not propagated.
        registrationStatus:
          clusterEntity?.registrationStatus ===
          RegistrationStatus.lockedManually
            ? RegistrationStatus.lockedManually
            : RegistrationStatus.unknown,
        pose: {
          pos: {
            x: 0,
            y: 0,
            z: 0,
          },
          scale: {
            x: 1,
            y: 1,
            z: 1,
          },
          rot: {
            x: 0,
            y: 0,
            z: 0,
            w: 1,
          },
        },
      };

      setIsProcessing(true);

      try {
        await createRevisionToAddClusterEntity(
          currentRevision,
          state,
          projectApiClient,
          newCluster,
        );

        dispatch(addEntities([...revisionEntities, newCluster]));
        await dispatch(loadRevisionDataThunk({ projectApiClient }));

        onExpandParentNode?.(parentId);
        setNameEditorAnchorEl(null);

        // Open the context menu on the newly created cluster
        dispatch(setEntityIdToOpenContextMenu(newCluster.id));
      } catch {
        // Re-enable the action so the user can retry on failure.
        setIsProcessing(false);
      }
      // Close the existing context menu after the cluster is created
      onClusterAddition?.();
    },
    [
      parentId,
      isProcessing,
      getState,
      clusterEntity,
      onClusterAddition,
      projectApiClient,
      dispatch,
      revisionEntities,
      onExpandParentNode,
    ],
  );

  const handleOpenNameEditor = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      if (isProcessing) {
        return;
      }
      setNameEditorAnchorEl(event.currentTarget);
    },
    [isProcessing],
  );

  const handleNameEditorClose = useCallback(() => {
    if (isProcessing) {
      return;
    }
    setNameEditorAnchorEl(null);
  }, [isProcessing]);

  const handleConfirmName = useCallback(
    async (name: string) => {
      const sanitizedName = sanitizeName(name);
      if (validateScanTreeElementName(sanitizedName, siblingEntityNames)) {
        return;
      }

      // Close the editor immediately to avoid validating against the newly created sibling.
      setNameEditorAnchorEl(null);
      await createCluster(sanitizedName);
    },
    [createCluster, siblingEntityNames],
  );

  const isNameEditorOpen = Boolean(nameEditorAnchorEl);

  return (
    <>
      <FaroMenuItem
        key="addCluster"
        label="Add Cluster"
        Icon={isProcessing ? SpinnerIcon : PlusIcon}
        sx={{ "& .MuiSvgIcon-root": { fontSize: "1.5rem" } }}
        onClick={handleOpenNameEditor}
        disabled={isProcessing || disabled}
      />
      <FaroMenu
        aria-label="add-cluster-name-menu"
        anchorEl={nameEditorAnchorEl}
        open={isNameEditorOpen}
        onClose={handleNameEditorClose}
        slotProps={slotProps}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClick={(event) => event.stopPropagation()}
        autoFocus={false}
        disableAutoFocusItem
        disableEnforceFocus
      >
        <MenuItem
          key="editClusterName"
          // Keep text selection behavior while editing.
          sx={{ cursor: "auto", userSelect: "auto" }}
        >
          <NoTranslate>
            <ActionTextField
              inputText={defaultClusterName}
              isInitiallyEditing
              onConfirmButtonClick={handleConfirmName}
              onCancelButtonClick={handleNameEditorClose}
              validate={(name) =>
                validateScanTreeElementName(
                  sanitizeName(name),
                  siblingEntityNames,
                )
              }
              fullWidth
              disableGutters
            />
          </NoTranslate>
        </MenuItem>
      </FaroMenu>
    </>
  );
}

/** @returns a spinner sized for the `Icon` slot of a FaroMenuItem */
function SpinnerIcon(): JSX.Element {
  return <CircularProgress size="1.25rem" />;
}

/**
 * Creates a registration revision to add a new cluster entity to the root of the scan tree.
 * On success, the revision is merged to the draft, otherwise it is canceled.
 *
 * @param currentRevision the currently active registration revision (the draft)
 * @param state the current application state
 * @param projectApiClient client to talk to the project API
 * @param clusterEntity the new cluster entity to add.
 */
async function createRevisionToAddClusterEntity(
  currentRevision: RegistrationRevision,
  state: RootState,
  projectApiClient: ProjectApi,
  clusterEntity: ClusterEntityRevision,
): Promise<void> {
  const editRevision = await revisionToEdit(
    currentRevision,
    projectApiClient,
    state,
  );

  await projectApiClient.createOrUpdateClusterEntitiesForRegistrationRevision({
    registrationRevisionId: editRevision.id,
    requestBody: [clusterEntity],
  });

  await applyRevision({ revision: editRevision, projectApiClient });
}

const DEFAULT_CLUSTER_NAME_PREFIX = "Cluster";
const DEFAULT_CLUSTER_NAME_REGEX = /^Cluster([1-9]\d*)$/;

/**
 * @param parentEntityId Parent entity id that the new cluster will be created under.
 * @param childEntities Direct children of the provided parent entity.
 * @returns The next default cluster name in the form `ClusterN`.
 */
export function getNextDefaultClusterName(
  parentEntityId: GUID | null,
  childEntities: CaptureTreeEntityRevision[],
): string {
  if (!parentEntityId) {
    return `${DEFAULT_CLUSTER_NAME_PREFIX}1`;
  }

  let maxClusterNumber = 0;

  for (const entity of childEntities) {
    const match = DEFAULT_CLUSTER_NAME_REGEX.exec(entity.name);
    if (!match) continue;

    const clusterNumber = Number.parseInt(match[1], 10);
    if (Number.isNaN(clusterNumber)) continue;

    maxClusterNumber = Math.max(maxClusterNumber, clusterNumber);
  }

  return `${DEFAULT_CLUSTER_NAME_PREFIX}${maxClusterNumber + 1}`;
}
